/**
 * AnnotationOverlay — UI for "select text → add note" in the collab editor.
 *
 * Two elements:
 *   1. A floating "💬 Anmerken" button that appears next to the
 *      selection when the user has highlighted non-empty text.
 *   2. A modal that opens when the button is clicked, showing the
 *      selected snippet + a textarea for the comment. Submit creates a
 *      `<section type="annotation">` via sections_create, which is
 *      drift-stable (block_id anchored) and supports reply threads
 *      through the federation-collab section_reply path.
 *
 * Anchor strategy (2026-06-12 drift fix):
 *   - We grab the block_id of the parent paragraph (`$from.parent.attrs.block_id`)
 *     and the offset INSIDE that paragraph (`$from.parentOffset`).
 *   - Plus the literal `anchor_quote` snippet as last-resort fallback.
 *   - When text is inserted ABOVE the annotation: block_id stays valid,
 *     paragraph re-renders at its new global position → annotation
 *     follows automatically.
 *   - When text is inserted INSIDE the same paragraph BEFORE the
 *     anchor: parentOffset stays valid (no global shift), still right.
 *   - When the paragraph itself is heavily rewritten: anchor_quote
 *     indexOf fallback in AnnotationHighlight.
 *
 * Cross-paragraph selections fall back to legacy REST so we don't lose
 * the rare case where the user spans a block boundary — those still
 * drift on inserts above but the snippet fallback handles most cases.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import type { Editor } from '@tiptap/react'
import { CONTENT_API } from '../config'
import { getAccessToken, getUser } from '../auth'

interface Props {
  editor: Editor | null
  postId: number
  /** Called after a successful annotation create, e.g. to refetch the list. */
  onCreated?: () => void
}

interface ButtonPosition {
  top: number
  left: number
}

interface SelectionInfo {
  text: string
  startPos: number
  endPos: number
  /** Drift-stable anchor when selection is inside ONE paragraph. */
  blockId: string | null
  parentOffsetStart: number
  parentOffsetEnd: number
}

/**
 * Walk the doc up to `pmPos`, summing the lengths of text nodes plus
 * one newline per paragraph/heading boundary so the count matches the
 * materialized text the server stores. Mirrors the pycrdt serializer's
 * behaviour roughly enough for selection-based annotations.
 */
function charPosFromPmPos(editor: Editor, pmPos: number): number {
  let charCount = 0
  let firstBlock = true
  editor.state.doc.descendants((node, nodePos) => {
    if (nodePos >= pmPos) return false
    if (node.type.name === 'paragraph' || node.type.name === 'heading') {
      if (!firstBlock) charCount += 1 // newline between blocks
      firstBlock = false
      return true
    }
    if (node.isText) {
      const remaining = pmPos - nodePos
      const take = Math.min(node.nodeSize, remaining)
      charCount += take
    }
    return true
  })
  return charCount
}

export function AnnotationOverlay({ editor, postId, onCreated }: Props) {
  const [buttonPos, setButtonPos] = useState<ButtonPosition | null>(null)
  const [selectionInfo, setSelectionInfo] = useState<SelectionInfo | null>(null)
  const [showDialog, setShowDialog] = useState(false)
  const [note, setNote] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const dialogRef = useRef<HTMLDivElement>(null)

  // Watch selection changes — show button when selection is non-empty.
  useEffect(() => {
    if (!editor) return
    const update = () => {
      const { from, to, empty } = editor.state.selection
      if (empty || showDialog) {
        setButtonPos(null)
        return
      }
      const text = editor.state.doc.textBetween(from, to, ' ').trim()
      if (text.length < 2) {
        setButtonPos(null)
        return
      }
      // Position the button just below the end of the selection in
      // viewport coords.
      try {
        const coords = editor.view.coordsAtPos(to)
        setButtonPos({
          top: coords.bottom + window.scrollY + 4,
          left: coords.left + window.scrollX,
        })
        // Drift-stable anchor (2026-06-12): same-paragraph selections
        // get block_id + parentOffset; cross-paragraph stays REST-only.
        const $from = editor.state.selection.$from
        const $to = editor.state.selection.$to
        let blockId: string | null = null
        let pStart = 0
        let pEnd = 0
        if ($from.parent === $to.parent) {
          const attrs = $from.parent.attrs as { block_id?: string | null } | undefined
          blockId = (attrs?.block_id as string) || null
          pStart = $from.parentOffset
          pEnd = $to.parentOffset
        }
        setSelectionInfo({
          text,
          startPos: charPosFromPmPos(editor, from),
          endPos: charPosFromPmPos(editor, to),
          blockId,
          parentOffsetStart: pStart,
          parentOffsetEnd: pEnd,
        })
      } catch {
        setButtonPos(null)
      }
    }
    editor.on('selectionUpdate', update)
    editor.on('blur', () => {
      // Small delay so a click on the button registers before the
      // selection-blur hides it.
      window.setTimeout(() => {
        if (!showDialog) setButtonPos(null)
      }, 150)
    })
    return () => {
      editor.off('selectionUpdate', update)
    }
  }, [editor, showDialog])

  const openDialog = useCallback(() => {
    setShowDialog(true)
    setButtonPos(null)
  }, [])

  const closeDialog = useCallback(() => {
    setShowDialog(false)
    setNote('')
  }, [])

  const submit = useCallback(async () => {
    if (!selectionInfo) return
    if (!note.trim()) return
    const token = getAccessToken()
    if (!token) return
    const user = getUser()
    const noteText = note.trim()
    const initiator = user
      ? `${user.first_name} ${user.last_name}`.trim() || user.email
      : 'Anon'
    setSubmitting(true)
    try {
      // Drift-stable path: same-paragraph selection with known block_id
      // → create as `<section type="annotation">` with block-relative
      // offsets so inserts above don't shift the anchor.
      let r: Response
      if (selectionInfo.blockId) {
        r = await fetch(
          `${CONTENT_API}/api/v1/posts/${postId}/sections/`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              type: 'annotation',
              attrs: {
                initiator,
                anchor_paragraph_id: selectionInfo.blockId,
                anchor_start: selectionInfo.parentOffsetStart,
                anchor_end: selectionInfo.parentOffsetEnd,
                anchor_quote: selectionInfo.text,
              },
              initial_text: `<main>${noteText}</main>`,
            }),
          }
        )
      } else {
        // Cross-paragraph selection: legacy REST path. char-positions
        // still drift but snippet-fallback handles most cases.
        r = await fetch(
          `${CONTENT_API}/api/v1/posts/${postId}/annotations/`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              start_pos: selectionInfo.startPos,
              end_pos: selectionInfo.endPos,
              note_text: noteText,
              author_id: user ? `user:${user.id}` : 'anon',
              author_name: initiator,
            }),
          }
        )
      }
      if (r.ok) {
        closeDialog()
        onCreated?.()
      } else {
        const errText = await r.text()
        alert(`Fehler beim Speichern: ${r.status} ${errText}`)
      }
    } catch (e) {
      alert(`Netzwerk-Fehler: ${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setSubmitting(false)
    }
  }, [selectionInfo, note, postId, closeDialog, onCreated])

  // Close on Escape
  useEffect(() => {
    if (!showDialog) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeDialog()
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) submit()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [showDialog, closeDialog, submit])

  return (
    <>
      {buttonPos && !showDialog && (
        <button
          type="button"
          onMouseDown={(e) => {
            // Prevent the editor from getting a blur event before our
            // click handler fires.
            e.preventDefault()
          }}
          onClick={openDialog}
          style={{
            position: 'absolute',
            top: buttonPos.top,
            left: buttonPos.left,
            zIndex: 100,
            padding: '4px 10px',
            background: '#222',
            color: '#fff',
            border: 'none',
            borderRadius: 4,
            fontSize: 12,
            cursor: 'pointer',
            boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
          }}
        >
          💬 Anmerken
        </button>
      )}

      {showDialog && selectionInfo && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 200,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) closeDialog()
          }}
        >
          <div
            ref={dialogRef}
            style={{
              background: '#fff',
              padding: 20,
              borderRadius: 8,
              maxWidth: 500,
              width: '90vw',
              boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
            }}
          >
            <h3 style={{ margin: '0 0 12px 0', fontSize: 16 }}>
              Anmerkung hinzufügen
            </h3>
            <blockquote
              style={{
                margin: '0 0 12px 0',
                padding: '8px 12px',
                background: '#f5f5f5',
                borderLeft: '3px solid #888',
                fontStyle: 'italic',
                fontSize: 14,
                maxHeight: 120,
                overflowY: 'auto',
              }}
            >
              {selectionInfo.text}
            </blockquote>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Deine Anmerkung dazu …"
              autoFocus
              style={{
                width: '100%',
                minHeight: 100,
                padding: 8,
                border: '1px solid #ccc',
                borderRadius: 4,
                fontFamily: 'inherit',
                fontSize: 14,
                boxSizing: 'border-box',
                resize: 'vertical',
              }}
            />
            <div
              style={{
                marginTop: 12,
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 8,
              }}
            >
              <button
                type="button"
                onClick={closeDialog}
                style={{
                  padding: '6px 14px',
                  background: 'transparent',
                  border: '1px solid #ccc',
                  borderRadius: 4,
                  cursor: 'pointer',
                }}
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={submitting || !note.trim()}
                style={{
                  padding: '6px 14px',
                  background: submitting ? '#999' : '#222',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 4,
                  cursor: submitting ? 'wait' : 'pointer',
                }}
              >
                {submitting ? 'Speichert …' : 'Anmerkung senden (⌘+Enter)'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
